import { LayerKey, STANDARD_BRANCH } from '@festina-life/api'
import { notEmpty, type Optional } from '@festina-life/core'
import { useTranslate } from '@festina-life/ui/hooks'
import {
  Icon,
  type IconName,
  PathBreadcrumb,
  type PathPart,
} from '@festina-life/ui/structure'
import { useMemo } from 'react'

import { useBaseBranchWorkspaces } from '../../hooks/useBaseBranchWorkspaces'
import { useScrollToPath } from '../../scenes/serverConfigurationItems/useScrollToPath'
import { useNavigationParams } from '../../system/useNavigationParams'
import { useConciseBranch } from '../../uiModels/branches/useConciseBranch'
import { useWorkspaceManagerContext } from '../../workspace/workspaceManagerContext'
import { useOpenActiveElement } from '../branchesPanel/useOpenActiveElement'
import { CategoryToIconMap } from '../branchNameWithStatus/helpers'
import { ConfigurationBreadcrumbPart } from './ConfigurationBreadcrumbPart'
import { useCurrentPackageName } from './useCurrentPackageName'

export const ConfigurationBreadcrumb = () => {
  const translate = useTranslate()
  const { server, branch, block, layer } = useNavigationParams()
  const { currentWorkspaceKey } = useWorkspaceManagerContext()
  const branchKey = useMemo(
    () => (branch ? { name: branch, server } : undefined),
    [branch, server],
  )
  const branchWorkspaces = useBaseBranchWorkspaces(branchKey)
  const currentPackageName = useCurrentPackageName()
  const scrollToPath = useScrollToPath()
  const openActiveElement = useOpenActiveElement()
  const workspaceCount = branchWorkspaces.length

  const conciseBranch = useConciseBranch({
    server,
    branchName: branch,
    shouldFallbackToStandard: false,
  })

  const isEditable = !!currentWorkspaceKey
  // Workspace segment is only shown when there are multiple workspaces (non-unified mode)
  const showWorkspace = isEditable && workspaceCount > 1
  const isOpenUnifiedWorkspaceBranch = isEditable && !showWorkspace

  const parts = useMemo<PathPart[]>(() => {
    const result: PathPart[] = []

    if (!branch && !currentWorkspaceKey) {
      return []
    }

    result.push({
      label: (
        <span className="flex items-center gap-1">
          <Icon name="CircleStack" />
          {server}
        </span>
      ),
      onClick: () => openActiveElement(server),
    })

    if (branch && branchKey) {
      let branchCategoryIconName: Optional<IconName>

      if (branch === STANDARD_BRANCH) {
        branchCategoryIconName = 'BuildingOffice'
      } else {
        branchCategoryIconName = conciseBranch?.branchCategory
          ? CategoryToIconMap[conciseBranch.branchCategory]
          : undefined
      }

      result.push({
        label: (
          <ConfigurationBreadcrumbPart
            iconName={branchCategoryIconName}
            iconClassName={
              isOpenUnifiedWorkspaceBranch ? 'text-white' : undefined
            }
            label={
              branch !== STANDARD_BRANCH ? branch : translate('branch.default')
            }
          />
        ),
        ...(isOpenUnifiedWorkspaceBranch && {
          backgroundClassName: 'bg-draft-600 dark:bg-draft-400',
          textClassName: 'text-white',
        }),
        onClick: () => openActiveElement(branchKey),
      })
    }

    if (showWorkspace && currentWorkspaceKey) {
      result.push({
        label: (
          <ConfigurationBreadcrumbPart
            iconName="Briefcase"
            label={currentWorkspaceKey.name}
          />
        ),
        backgroundClassName: 'bg-draft-600 dark:bg-draft-400',
        textClassName: 'text-white',
        onClick: () => openActiveElement(currentWorkspaceKey),
      })
    }

    result.push({
      label: (
        <ConfigurationBreadcrumbPart
          iconName="Square3Stack3D"
          label={layer.layerId ?? translate('layer.null')}
        />
      ),
      onClick: () =>
        scrollToPath(new LayerKey({ layerId: layer.layerId, tenantId: null })),
    })

    if (layer.tenantId) {
      result.push({
        label: (
          <ConfigurationBreadcrumbPart
            iconName="Square3Stack3D"
            label={layer.tenantId}
          />
        ),
        onClick: () => scrollToPath(layer),
      })
    }

    const packageParts = currentPackageName?.split('.')

    result.push(
      ...(packageParts
        ?.map((packagePart, partIndex) =>
          partIndex > 0 ||
          packagePart.localeCompare(layer.layerId ?? '', undefined, {
            sensitivity: 'base',
          }) !== 0
            ? {
                label: (
                  <span className="flex items-center gap-1">
                    <Icon name="Folder" />
                    {packagePart}
                  </span>
                ),
                onClick: () =>
                  scrollToPath(
                    layer,
                    packageParts.slice(0, partIndex + 1).join('.'),
                  ),
              }
            : undefined,
        )
        .filter(notEmpty) ?? []),
    )

    result.push({
      label: <ConfigurationBreadcrumbPart iconName="Cube" label={block} />,
    })

    return result
  }, [
    server,
    branch,
    block,
    translate,
    layer,
    showWorkspace,
    currentWorkspaceKey,
    conciseBranch,
    isOpenUnifiedWorkspaceBranch,
    currentPackageName,
    scrollToPath,
    openActiveElement,
    branchKey,
  ])

  return <PathBreadcrumb parts={parts} showBorder={false} size="compact" />
}
